<template>
	<div class="map-tooltip" :style="tooltipStyle" v-if="enabled && hoveredTyped != null">
		<BaseStationStatusTooltip
			v-if="hoveredBaseStationData != null"
			:hoveredFeatureData="hoveredBaseStationData"
			featureType="baseStation"
		/>
		<FailedGetterTooltip
			v-else-if="hoveredGetterFailedData != null"
			:hoveredFeatureData="hoveredGetterFailedData"
			featureType="getterFailed"
		/>
		<ActiveGetterTooltip
			v-else-if="hoveredGetterActiveData != null"
			:hoveredFeatureData="hoveredGetterActiveData"
			featureType="getterActive"
		/>
		<RoverHistoryTooltip
			v-else-if="hoveredRoverHistory != null"
			:hoveredFeatureData="hoveredRoverHistory.data"
			:featureType="hoveredRoverHistory.featureType"
		/>
		<QualityRectangleTooltip
			v-else-if="mapMode === MAP_MODE.coverage && hoveredQualityRectangleData != null"
			:hoveredFeatureData="hoveredQualityRectangleData"
		/>
	</div>
</template>

<script lang="ts">
import { computed, toRef } from 'vue';
import type { PropType } from 'vue';

import { FEATURE_TYPE, MAP_MODE } from '@/components/map/consts/mapFeatureConsts';
import type {
	MapMode,
	RoverHistoryCurrentFeatureData,
	RoverHistoryFragmentFeatureData,
	TypedMapFeature,
} from '@/components/map/types/mapFeatureTypes';
import { isTypedMapFeatureOfType } from '@/components/map/utils/mapFeatureGuards';

import BaseStationStatusTooltip from '@/components/map/BaseStationStatusTooltip.vue';
import ActiveGetterTooltip from '@/components/map/tooltips/ActiveGetterTooltip.vue';
import FailedGetterTooltip from '@/components/map/tooltips/FailedGetterTooltip.vue';
import RoverHistoryTooltip from '@/components/map/tooltips/RoverHistoryTooltip.vue';
import QualityRectangleTooltip from '@/components/map/tooltips/QualityRectangleTooltip.vue';


type TooltipStyle = {
	left: string;
	top: string;
};

type RoverHistoryHover = {
	data: RoverHistoryCurrentFeatureData | RoverHistoryFragmentFeatureData;
	featureType: 'roverHistoryCurrent' | 'roverHistoryFragment';
};

export default {
	components: {
		BaseStationStatusTooltip,
		ActiveGetterTooltip,
		FailedGetterTooltip,
		RoverHistoryTooltip,
		QualityRectangleTooltip,
	},
	props: {
		hoveredTyped: {
			type: Object as PropType<TypedMapFeature | null>,
			default: null,
		},
		enabled: {
			type: Boolean,
			required: true,
		},
		tooltipStyle: {
			type: Object as PropType<TooltipStyle>,
			required: true,
		},
		mapMode: {
			type: String as PropType<MapMode>,
			required: true,
		},
	},
	setup(props) {
		const hoveredTypedRef = toRef(props, 'hoveredTyped');
		const hoveredBaseStationData = computed(() => {
			const typed = hoveredTypedRef.value;
			return isTypedMapFeatureOfType(typed, FEATURE_TYPE.baseStation) ? typed.featureData : null;
		});
		const hoveredGetterFailedData = computed(() => {
			const typed = hoveredTypedRef.value;
			return isTypedMapFeatureOfType(typed, FEATURE_TYPE.getterFailed) ? typed.featureData : null;
		});
		const hoveredGetterActiveData = computed(() => {
			const typed = hoveredTypedRef.value;
			return isTypedMapFeatureOfType(typed, FEATURE_TYPE.getterActive) ? typed.featureData : null;
		});
		const hoveredQualityRectangleData = computed(() => {
			const typed = hoveredTypedRef.value;
			return isTypedMapFeatureOfType(typed, FEATURE_TYPE.qualityRectangle) ? typed.featureData : null;
		});
		const hoveredRoverHistory = computed((): RoverHistoryHover | null => {
			const typed = hoveredTypedRef.value;
			if (isTypedMapFeatureOfType(typed, FEATURE_TYPE.roverHistoryCurrent)) {
				return {
					data: typed.featureData,
					featureType: typed.type,
				};
			}
			if (isTypedMapFeatureOfType(typed, FEATURE_TYPE.roverHistoryFragment)) {
				return {
					data: typed.featureData,
					featureType: typed.type,
				};
			}
			return null;
		});
		return {
			hoveredBaseStationData,
			hoveredGetterFailedData,
			hoveredGetterActiveData,
			hoveredQualityRectangleData,
			hoveredRoverHistory,
			MAP_MODE,
		};
	},
}
</script>

<style scoped>
@import '@/assets/map-tooltip.css';
</style>
